import { Section } from "Pages/TestEvaluation/StudentEvaluation/types";
import React, { useEffect } from "react";
import styled from "styled-components";
import { ExclamationCircleOutlined } from "@ant-design/icons";

const Container = styled.div`
  background: #fffcfc;
  border-bottom: 1px solid #ffecec;
  padding: 16px 20px;
  margin: -20px -20px 20px -20px;
  border-radius: 12px 12px 0 0;
`;

const HeaderRow = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 16px;
`;

export const StyledTitle = styled.div`
  font-size: 12px;
  font-weight: 800;
  color: #ff4d4f;
  text-transform: uppercase;
  letter-spacing: 0.5px;
  display: flex;
  align-items: center;
  gap: 8px;
`;

const PendingText = styled.div`
  font-size: 14px;
  font-weight: 700;
  color: #ff7875;
`;

const StyledChallengedQuestionsList = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 12px;

  .question-item {
    display: flex;
    align-items: center;
    gap: 8px;
    font-size: 12px;
    font-weight: 800;
    color: #ff4d4f;
    cursor: pointer;
    padding: 4px 6px;
    border-radius: 20px;
    background: #ffffff;
    border: 1px solid #ffccc7;
    transition: all 0.2s ease;

    .dot {
      width: 8px;
      height: 8px;
      border-radius: 50%;
      background: #ff4d4f;
    }

    .q-code {
      font-size: 14px;
      font-weight: 500;
      color: #ff7875;
    }

    &:hover {
      background: #fff1f0;
      border-color: #ffa39e;
    }

    &.active {
      background: #fff1f0;
      border-color: #ff4d4f;
      box-shadow: 0 0 0 2px rgba(255, 77, 79, 0.1);
    }
  }
`;

const ChallengedZoneTab = ({
  setSelectedQuestionId,
  selectedQuestionId,
  setActiveSectionKeys,
  challengedQuestions,
  subjects,
  isSectionWisePreference,
}: any) => {
  useEffect(() => {
    if (!challengedQuestions || !challengedQuestions.length) return;

    // Only auto-open if we don't have a selection
    if (selectedQuestionId === null) {
      const firstChallengedQuestionId =
        challengedQuestions[0].id ||
        challengedQuestions[0].questionSubmissionId;

      setSelectedQuestionId(firstChallengedQuestionId);

      const sectionIdsToOpen: string[] = [];
      subjects?.forEach((subject: any) => {
        subject?.sections?.forEach((section: Section, index: number) => {
          const hasQuestion = section.questions?.some(
            (q) =>
              (q.id || q.questionSubmissionId) === firstChallengedQuestionId
          );
          if (hasQuestion) {
            const key = String(
              !isSectionWisePreference ? index : section.sectionSubmissionId
            );
            sectionIdsToOpen.push(key);
          }
        });
      });

      if (sectionIdsToOpen.length > 0) {
        setActiveSectionKeys((prev: any) => {
          const prevArray = Array.isArray(prev)
            ? prev
            : ([prev].filter(Boolean) as string[]);
          return Array.from(new Set([...prevArray, ...sectionIdsToOpen]));
        });
      }
    }
  }, [
    challengedQuestions,
    subjects,
    selectedQuestionId,
    setSelectedQuestionId,
    setActiveSectionKeys,
  ]);

  const handleChallengedClick = (q: any) => {
    const targetQuestionId = q.id || q.questionSubmissionId;
    setSelectedQuestionId(targetQuestionId);

    const sectionIdsToOpen: string[] = [];
    subjects?.forEach((subject: any) => {
      subject?.sections?.forEach((section: Section, index: number) => {
        const hasQuestion = section.questions?.some(
          (sq) => (sq.id || sq.questionSubmissionId) === targetQuestionId
        );
        if (hasQuestion) {
          const key = String(
            !isSectionWisePreference ? index : section.sectionSubmissionId
          );
          sectionIdsToOpen.push(key);
        }
      });
    });

    if (sectionIdsToOpen.length > 0) {
      setActiveSectionKeys((prev: any) => {
        const prevArray = Array.isArray(prev)
          ? prev
          : ([prev].filter(Boolean) as string[]);
        return Array.from(new Set([...prevArray, ...sectionIdsToOpen]));
      });
    }
    setTimeout(() => {
      const element = document.getElementById(
        `question-panel-${targetQuestionId}`
      );
      if (element) {
        element.scrollIntoView({ behavior: "smooth", block: "center" });
      }
    }, 200);
  };

  if (!challengedQuestions || challengedQuestions.length === 0) {
    return null;
  }

  return (
    <Container>
      <HeaderRow>
        <StyledTitle>
          <ExclamationCircleOutlined />
          Challenged Questions
        </StyledTitle>
        <PendingText>{challengedQuestions.length} Pending</PendingText>
      </HeaderRow>
      <StyledChallengedQuestionsList>
        {challengedQuestions.map((q: any) => {
          const qId = q.id || q.questionSubmissionId;
          const displayQNum = q.questionNumber || qId;
          return (
            <div
              key={qId}
              className={`question-item ${
                selectedQuestionId === qId ? "active" : ""
              }`}
              onClick={() => handleChallengedClick(q)}
            >
              <div className="dot" />
              <span>Q{displayQNum}</span>
            </div>
          );
        })}
      </StyledChallengedQuestionsList>
    </Container>
  );
};

export default ChallengedZoneTab;
